import React, { useState } from 'react';
import { Heart, ArrowUpRight, Coffee, Music2, Radio, Sparkles } from 'lucide-react';
import { HOME } from '@/constants/testIds';

const PAYPAL_URL = 'https://www.paypal.com/donate/?hosted_button_id=LPC97GTZDJJSU';

/**
 * DonationBanner — high-conversion redesign.
 *
 * Strategy (no begging):
 *   - Reciprocity: emphasize what listener already gets for free.
 *   - Anchoring: 4 specific small amounts framed as little tokens, not "donations".
 *   - Social proof: "Independent since 2017".
 *   - Action: one bold PayPal button that visually telegraphs warmth.
 */

const TIERS = [
  { amount: '€3',  label: 'A thank-you',  tag: 'A small word of thanks',     icon: Heart   },
  { amount: '€5',  label: 'A friend',      tag: 'Stand with the station',     icon: Music2  },
  { amount: '€10', label: 'A helping hand',tag: 'Truly meaningful support',   icon: Sparkles},
  { amount: '€25', label: 'A patron',      tag: 'Keep the records spinning',  icon: Coffee  },
];

const DonationBanner = () => {
  const [hovered, setHovered] = useState(null);

  return (
    <section className="relative">
      <div
        data-testid={HOME.paypalCta + '-section'}
        className="relative overflow-hidden rounded-3xl border border-amber/30 donate-glow"
        style={{ background: 'linear-gradient(135deg, #1C1917 0%, #292524 50%, #1C1917 100%)' }}
      >
        {/* Decorative spotlights */}
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full pointer-events-none"
          style={{ background: 'radial-gradient(closest-side, rgba(245,158,11,0.20), transparent)' }} />
        <div className="absolute -bottom-24 -left-24 w-96 h-96 rounded-full pointer-events-none"
          style={{ background: 'radial-gradient(closest-side, rgba(159,18,57,0.20), transparent)' }} />

        <div className="relative p-8 md:p-12">
          <div className="grid lg:grid-cols-12 gap-10 items-start">
            {/* Copy */}
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-amber/40 bg-amber/5 mb-5">
                <Radio className="w-3.5 h-3.5 text-amber" />
                <span className="text-[10px] tracking-[0.25em] uppercase text-amber font-bold">
                  On air since December 2017
                </span>
              </div>
              <h2 className="font-display text-3xl sm:text-4xl md:text-5xl text-cream leading-[1.05]">
                Eight years on the dial,
                <br />
                <span className="font-serif-italic italic text-amber">kept turning by listeners like you.</span>
              </h2>
              <p className="mt-5 text-cream/70 font-body text-base md:text-lg leading-relaxed max-w-xl">
                Majestic Jukebox is an independent, commercial-free station — no advertising,
                no labels, no investors. Streaming, hosting and music licensing have always cost
                more than what kindly comes in. If our records have brought you a smile, a tune
                to hum along to or a quiet hour of company, a gentle gesture — once, or whenever
                you feel like it — keeps the music turning for the next listener.
              </p>
              <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-amber-light/70 font-body">
                <span>✓ Secure via PayPal</span>
                <span>·</span>
                <span>✓ Any currency, any amount</span>
                <span>·</span>
                <span>✓ One-time or whenever</span>
                <span>·</span>
                <span>✓ Goes straight to the station</span>
              </div>
            </div>

            {/* CTA */}
            <div className="lg:col-span-5">
              {/* Amount tiles */}
              <div className="grid grid-cols-2 gap-3">
                {TIERS.map((t, i) => {
                  const Icon = t.icon;
                  return (
                    <a
                      key={i}
                      data-testid={`donate-tier-${t.amount.replace(/[^0-9]/g, '')}`}
                      href={PAYPAL_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      onMouseEnter={() => setHovered(i)}
                      onMouseLeave={() => setHovered(null)}
                      className={`
                        relative rounded-2xl border p-4 text-left transition-all duration-300
                        ${hovered === i
                          ? 'border-amber bg-amber/10 -translate-y-0.5'
                          : 'border-amber/20 bg-vinyl/50 hover:border-amber/50'}
                      `}
                    >
                      <Icon className="w-4 h-4 text-amber mb-2" />
                      <div className="font-display text-2xl text-cream leading-none">{t.amount}</div>
                      <div className="mt-1 text-xs text-cream/60 font-body">{t.tag}</div>
                    </a>
                  );
                })}
              </div>

              {/* Main button */}
              <a
                data-testid={HOME.paypalCta}
                href={PAYPAL_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="
                  mt-4 group relative w-full flex items-center justify-center gap-3
                  px-6 py-5 rounded-full
                  bg-[#FFC439] text-[#0C0A09]
                  font-body font-bold text-base tracking-wide
                  shadow-[0_15px_40px_-10px_rgba(255,196,57,0.6)]
                  hover:shadow-[0_20px_60px_-10px_rgba(255,196,57,0.85)]
                  hover:scale-[1.02] active:scale-95
                  transition-all duration-300
                  ring-4 ring-amber/20 hover:ring-amber/40
                "
              >
                <span className="font-display text-xl">Support the station</span>
                <span className="opacity-70">with PayPal</span>
                <ArrowUpRight className="w-5 h-5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>

              <div className="mt-3 text-center text-[11px] text-cream/45 font-body">
                Or choose your own amount on PayPal — every contribution matters.
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DonationBanner;
